import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LakshLogo } from '@/components/LakshLogo';
import axios from 'axios';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import {
  Users,
  LayoutDashboard,
  Calendar,
  IndianRupee,
  Briefcase,
  LogOut,
  AlertTriangle,
  Check,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/ca/dashboard' },
  { icon: Users, label: 'Clients', path: '/ca/clients' },
  { icon: Calendar, label: 'Compliance', path: '/ca/compliance' },
  { icon: IndianRupee, label: 'Revenue', path: '/ca/revenue' },
];

export default function CACompliancePage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [user, setUser] = useState(null);
  const [firm, setFirm] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('laksh_ca_token');
    const userData = localStorage.getItem('laksh_ca_user');
    const firmData = localStorage.getItem('laksh_ca_firm');
    
    if (!token) {
      navigate('/ca/login');
      return;
    }
    
    if (userData) setUser(JSON.parse(userData));
    if (firmData) setFirm(JSON.parse(firmData));
    
    fetchCompliance(token);
  }, [navigate]);

  const fetchCompliance = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ca/compliance-calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error('Compliance error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('laksh_ca_token');
    localStorage.removeItem('laksh_ca_user');
    localStorage.removeItem('laksh_ca_firm');
    navigate('/ca/login');
  };

  const filteredItems = items.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'gstr1') return item.filing_type === 'GSTR-1';
    if (filter === 'gstr3b') return item.filing_type === 'GSTR-3B';
    if (filter === 'overdue') return item.status === 'overdue';
    return true;
  });

  const overdueCount = items.filter(i => i.status === 'overdue').length;
  const pendingCount = items.filter(i => ['pending', 'due_soon'].includes(i.status)).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06100A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D9E75] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#06100A]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0a1a12] border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <LakshLogo height={32} />
          <div className="mt-2 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#1D9E75]" />
            <span className="text-xs text-[#1D9E75] font-medium">CA Portal</span>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              data-testid={`ca-nav-${item.label.toLowerCase()}`}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-gray-400 transition-colors hover:bg-white/5 hover:text-white ${
                item.path === '/ca/compliance' ? 'bg-[#1D9E75]/20 text-[#1D9E75]' : ''
              }`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#1D9E75]/20 rounded-full flex items-center justify-center">
              <span className="text-[#1D9E75] font-semibold">{user?.name?.charAt(0) || 'C'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{firm?.name}</p>
            </div>
          </div>
          <FeedbackDialog portal="ca" token={localStorage.getItem('laksh_ca_token')} triggerClassName="w-full px-4 py-2 mb-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5" label="Send feedback" />
          <Button data-testid="ca-logout-btn" variant="ghost" onClick={handleLogout} className="w-full text-gray-400 hover:text-white hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">Compliance Calendar</h1>
            <p className="text-gray-400">Track GST filing deadlines for all clients</p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                  <div>
                    <p className="text-3xl font-bold text-red-400 font-mono">{overdueCount}</p>
                    <p className="text-sm text-red-300">Overdue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#EF9F27]/10 border-[#EF9F27]/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-[#EF9F27]" />
                  <div>
                    <p className="text-3xl font-bold text-[#EF9F27] font-mono">{pendingCount}</p>
                    <p className="text-sm text-[#EF9F27]/80">Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/15">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-gray-300" />
                  <div>
                    <p className="text-3xl font-bold text-gray-200 font-mono" data-testid="ca-compliance-gstin-missing-count">{items.filter(i => i.status === 'gstin_missing').length}</p>
                    <p className="text-sm text-gray-400">GSTIN missing</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#1D9E75]/10 border-[#1D9E75]/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Check className="w-8 h-8 text-[#1D9E75]" />
                  <div>
                    <p className="text-3xl font-bold text-[#1D9E75] font-mono">{items.length}</p>
                    <p className="text-sm text-[#1D9E75]/80">Total Filings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            {['all', 'gstr1', 'gstr3b', 'overdue'].map((f) => (
              <Button
                key={f}
                data-testid={`ca-compliance-filter-${f}`}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                className={filter === f 
                  ? 'bg-[#1D9E75] text-white' 
                  : 'border-white/20 text-gray-400 hover:text-white'
                }
              >
                {f === 'all' ? 'All' : f === 'gstr1' ? 'GSTR-1' : f === 'gstr3b' ? 'GSTR-3B' : 'Overdue'}
              </Button>
            ))}
          </div>

          {/* Compliance Items */}
          <div className="space-y-3">
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <Card key={index} className={`bg-[#0a1a12] border-white/10 ${
                  item.status === 'overdue' ? 'border-l-4 border-l-red-500' : ''
                }`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          item.filing_type === 'GSTR-1' ? 'bg-blue-500/20' : 'bg-[#EF9F27]/20'
                        }`}>
                          <span className={`font-bold text-xs ${
                            item.filing_type === 'GSTR-1' ? 'text-blue-400' : 'text-[#EF9F27]'
                          }`}>
                            {item.filing_type === 'GSTR-1' ? 'G1' : '3B'}
                          </span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{item.business_name}</p>
                          <p className="text-sm text-gray-500">{item.client_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-mono">{item.due_date}</p>
                        <p className="text-sm text-gray-500">{item.period}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.status === 'overdue' ? 'bg-red-500/20 text-red-400' :
                          item.status === 'due_soon' ? 'bg-[#EF9F27]/30 text-[#EF9F27]' :
                          item.status === 'pending' ? 'bg-[#EF9F27]/20 text-[#EF9F27]' :
                          item.status === 'gstin_missing' ? 'bg-gray-500/20 text-gray-300' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {item.status === 'gstin_missing' ? 'GSTIN missing' : item.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="bg-[#0a1a12] border-white/10 p-12">
                <div className="text-center">
                  <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No compliance items</h3>
                  <p className="text-gray-400">All caught up! No filings pending.</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
